import { ReactNode } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { FormikValues } from 'formik';
import AddIcon from '@mui/icons-material/Add';
import { Button, Typography } from '@mui/material';
import { DataFieldFormValue } from '@toolpad/core';

import { UploadFile } from 'components';
import { UploadFileProps } from 'components/upload-file/types';
import {
  Form,
  Nullable,
  FormField,
  InputType,
  FormBlock,
  InputOptions,
  RadioOptions,
  FormFieldType,
  SelectOptions,
  CheckboxOptions,
  CsvLookupPreset,
  FieldArrayLayout,
  CsvLookupOptions,
  FieldArrayOptions,
  FormFieldTypeTitle,
  ScaleRatingOptions,
  OutcomesFieldOptions,
  OutcomeFieldsOptions,
  DataFieldRenderFormFieldWithLabel,
} from 'types';

import { omit } from './object';
import { isUploadFile } from './file';
import { normalizeScaleRatingOptions } from './scale-rating';
import { mapFormField, mapFormBlocks } from './form-field-tree';
import { generateFormFieldsValidationSchema } from './validation';
import { normalizeOptionalStrings } from './normalize-optional-strings';

export const createFieldArray =
  <T,>(
    defaultItem: () => T,
    renderItem: (
      item: T,
      index: number,
      onChange: (newItem: T) => void,
      onRemove: () => void,
      count: number
    ) => ReactNode
  ) =>
  ({
    value,
    error,
    onChange,
    title = 'Item',
  }: {
    title?: string;
    error?: Nullable<string>;
    value: T[] | DataFieldFormValue;
    onChange: (value: DataFieldFormValue) => void;
  }) => {
    const array: T[] = Array.isArray(value) ? (value as T[]) : [];

    const emit = (newArr: T[]) => onChange(newArr as DataFieldFormValue);

    const handleAdd = () => {
      emit([...array, defaultItem()]);
    };

    const handleRemove = (i: number) => {
      emit(array.filter((_, idx) => idx !== i));
    };

    const handleItemChange = (i: number, newItem: T) => {
      const copy = [...array];
      copy[i] = newItem;
      emit(copy);
    };

    return (
      <div className="flex w-full flex-col gap-4">
        <div className="flex flex-col gap-4">
          {array.map((item, index) => (
            <div key={index}>
              {renderItem(
                item,
                index,
                (newItem) => handleItemChange(index, newItem),
                () => handleRemove(index),
                array.length
              )}
            </div>
          ))}
        </div>

        {error && (
          <Typography color="error" variant="body2">
            {error}
          </Typography>
        )}

        <div>
          <Button onClick={handleAdd} startIcon={<AddIcon />}>
            Add {title.toLowerCase()}
          </Button>
        </div>
      </div>
    );
  };

type FileRenderFormFieldParams =
  Parameters<DataFieldRenderFormFieldWithLabel>[0] & {
    accept?: UploadFileProps['accept'];
    allowedFileSize?: UploadFileProps['allowedFileSize'];
  };

export const FileRenderFormField = ({
  value,
  error,
  label,
  accept,
  onChange,
  allowedFileSize,
}: FileRenderFormFieldParams) => {
  const resolvePreviewSrc = () => {
    if (!value) return undefined;
    if (typeof value === 'string') return value;
    if (isUploadFile(value)) return value.src;
    if (typeof value === 'object' && value !== null && 'previewUrl' in value) {
      return (value as { previewUrl?: string | null }).previewUrl ?? undefined;
    }
    return undefined;
  };

  return (
    <UploadFile
      withoutCrop
      error={error}
      label={label}
      accept={accept}
      src={resolvePreviewSrc()}
      allowedFileSize={allowedFileSize}
      setFile={(file) => onChange(file as any)}
    />
  );
};

export const getInitialValuesForFields = (fields: FormField[]) => {
  let initialValues: FormikValues = {};
  for (const field of fields) {
    if (
      field.type === FormFieldType.PAGE_BREAK ||
      field.type === FormFieldType.HEADING ||
      field.type === FormFieldType.TEXT
    ) {
      continue;
    } else if (field.type === FormFieldType.OUTCOMES) {
      initialValues[field.id] = {
        outcomes: [],
        orderedIds: [],
      };
    } else if (field.type === FormFieldType.OUTCOME_FIELDS) {
      if (field.options?.fields) {
        initialValues = {
          ...initialValues,
          ...getInitialValuesForFields(field.options.fields),
        };
      }
    } else if (field.type === FormFieldType.SCALE_RATING) {
      initialValues[field.id] = field.options?.options?.map(() => null) ?? [];
    } else if (field.type === FormFieldType.FIELD_ARRAY) {
      if (field.options?.fields) {
        initialValues = {
          ...initialValues,
          ...getInitialValuesForFields(field.options?.fields),
        };
      }
    } else if (
      field.type === FormFieldType.CHECKBOX ||
      (field.type === FormFieldType.SELECT && !!field.options?.multiple)
    ) {
      initialValues[field.id] = [];
    } else if (
      field.type === FormFieldType.SELECT ||
      field.type === FormFieldType.RADIO
    ) {
      initialValues[field.id] = null;
    } else if (field.type === FormFieldType.CSV_LOOKUP) {
      initialValues[field.id] = [];
    } else {
      initialValues[field.id] = '';
    }
  }
  return initialValues;
};

export const getInitialValuesForForm = (form: Form) =>
  getInitialValuesForFields(form.data.blocks.map((b) => b.fields).flat());

export const getDataForFormikForm = (form: Form) => ({
  initialValues: getInitialValuesForForm(form),
  validationSchema: generateFormFieldsValidationSchema(
    form.data.blocks.map((b) => b.fields).flat()
  ),
});

export const getDataForFormikFormFromFields = (fields: FormField[]) => ({
  initialValues: getInitialValuesForFields(fields),
  validationSchema: generateFormFieldsValidationSchema(fields),
});

export const getDefaultInputOptions = (): InputOptions => ({
  integer: undefined,
  maxValue: undefined,
  minValue: undefined,
  type: InputType.Text,
  minLength: undefined,
  maxLength: undefined,
});

const getDefaultSelectOptions = (): SelectOptions => ({
  options: [''],
  multiple: false,
});

const getDefaultRadioOptions = (): RadioOptions => ({ options: [''] });

const getDefaultCheckboxOptions = (): CheckboxOptions => ({ options: [''] });

const getDefaultFieldArrayOptions = (): FieldArrayOptions => ({
  fields: [getDefaultField()],
  layout: FieldArrayLayout.VERTICAL,
});

const getDefaultScaleRatingOptions = (): ScaleRatingOptions => ({
  minValue: 1,
  maxValue: 7,
  options: [''],
  valueLabels: [],
});

const getDefaultOutcomesOptions = (): OutcomesFieldOptions => ({
  outcomes: [],
  conditions: [],
  conditionValue: '',
});

const getDefaultOutcomeFieldsOptions = (): OutcomeFieldsOptions => ({
  sourceFieldId: '',
  sourceCourseStepId: '',
  maxOutcomeCount: undefined,
  fields: [getDefaultField()],
});
const getDefaultCsvLookupOptions = (): CsvLookupOptions => ({
  preset: CsvLookupPreset.SCHOOL_LIST,
});

export function getDefaultField(
  type: FormFieldType = FormFieldType.INPUT
): FormField {
  const baseField = {
    type,
    label: '',
    id: uuidv4(),
    placeholder: '',
    required: false,
  };

  switch (type) {
    case FormFieldType.FIELD_ARRAY:
      return {
        ...baseField,
        type: FormFieldType.FIELD_ARRAY,
        options: getDefaultFieldArrayOptions(),
      };
    case FormFieldType.SELECT:
      return {
        ...baseField,
        type: FormFieldType.SELECT,
        options: getDefaultSelectOptions(),
      };
    case FormFieldType.SCALE_RATING:
      return {
        ...baseField,
        type: FormFieldType.SCALE_RATING,
        options: getDefaultScaleRatingOptions(),
      };
    case FormFieldType.RADIO:
      return {
        ...baseField,
        type: FormFieldType.RADIO,
        options: getDefaultRadioOptions(),
      };
    case FormFieldType.CHECKBOX:
      return {
        ...baseField,
        type: FormFieldType.CHECKBOX,
        options: getDefaultCheckboxOptions(),
      };
    case FormFieldType.OUTCOMES:
      return {
        ...baseField,
        type: FormFieldType.OUTCOMES,
        options: getDefaultOutcomesOptions(),
      };
    case FormFieldType.OUTCOME_FIELDS:
      return {
        ...baseField,
        type: FormFieldType.OUTCOME_FIELDS,
        options: getDefaultOutcomeFieldsOptions(),
      };
    case FormFieldType.CSV_LOOKUP:
      return {
        ...baseField,
        type: FormFieldType.CSV_LOOKUP,
        options: getDefaultCsvLookupOptions(),
      };
    case FormFieldType.INPUT:
      return {
        ...baseField,
        type: FormFieldType.INPUT,
        options: getDefaultInputOptions(),
      };
    case FormFieldType.PAGE_BREAK:
      return {
        ...baseField,
        options: {},
        label: 'Page break',
        type: FormFieldType.PAGE_BREAK,
      };
    case FormFieldType.TEXT:
      return {
        ...omit(baseField, ['placeholder']),
        text: '',
        options: {},
        label: 'Text',
        type: FormFieldType.TEXT,
      };
    case FormFieldType.HEADING:
      // A heading title must be entered explicitly before the form can save.
      return {
        ...omit(baseField, ['placeholder']),
        options: {},
        subtitle: '',
        type: FormFieldType.HEADING,
      };
  }
}

export function getDefaultBlock(): FormBlock {
  return {
    name: '',
    id: uuidv4(),
    fields: [getDefaultField()],
  };
}

export const getInitialBlocks = (blocks?: FormBlock[]): FormBlock[] =>
  blocks || [getDefaultBlock()];

const normalizeInputId = (inputId?: string | number | null) => {
  if (inputId === '' || inputId === null || inputId === undefined) {
    return undefined;
  }
  return inputId;
};

const normalizeOptionalText = (value?: string | null) => {
  const trimmed = value?.trim();
  return trimmed || undefined;
};

const normalizeFormField = (field: FormField): FormField => {
  const normalizedInputId = normalizeInputId(field.inputId);
  const normalizedReferenceCode = normalizeOptionalText(field.referenceCode);

  let normalizedField: FormField =
    field.type === FormFieldType.TEXT ? { ...field, label: 'Text' } : field;
  if (field.type === FormFieldType.OUTCOMES && field.options) {
    normalizedField = {
      ...field,
      options: {
        ...field.options,
        conditionValue:
          normalizeOptionalText(field.options.conditionValue) ?? '',
      },
    };
  } else if (field.type === FormFieldType.SCALE_RATING && field.options) {
    normalizedField = {
      ...field,
      options: normalizeScaleRatingOptions(field.options),
    };
  } else if (
    (field.type === FormFieldType.SELECT ||
      field.type === FormFieldType.RADIO ||
      field.type === FormFieldType.CHECKBOX) &&
    field.options
  ) {
    const normalizedAnswerValues = normalizeOptionalStrings(
      field.options.answerValues,
      field.options.options.length
    );
    const normalizedOtherAnswerValue = field.options.allowOther
      ? normalizeOptionalText(field.options.otherAnswerValue)
      : undefined;
    const choiceOptions = {
      ...omit(field.options, ['otherAnswerValue']),
      ...(normalizedOtherAnswerValue
        ? { otherAnswerValue: normalizedOtherAnswerValue }
        : {}),
    };
    normalizedField = {
      ...field,
      options: normalizedAnswerValues
        ? {
            ...choiceOptions,
            answerValues: normalizedAnswerValues,
          }
        : omit(choiceOptions, ['answerValues']),
    } as FormField;
  }

  if (
    normalizedInputId === undefined ||
    normalizedReferenceCode === undefined
  ) {
    const rest = { ...(normalizedField as FormField) };
    if (normalizedInputId === undefined) {
      delete rest.inputId;
    }
    if (normalizedReferenceCode === undefined) {
      delete rest.referenceCode;
    } else {
      rest.referenceCode = normalizedReferenceCode;
    }
    return rest;
  }

  return {
    ...normalizedField,
    inputId: normalizedInputId,
    referenceCode: normalizedReferenceCode,
  };
};

export const normalizeFormFieldForPayload = (field: FormField): FormField =>
  mapFormField(field, normalizeFormField);

export const normalizeFormBlocksForPayload = (blocks: FormBlock[]) =>
  mapFormBlocks(blocks, normalizeFormField);

export const typeOptions = Object.values(FormFieldType).map((value) => ({
  value,
  label: FormFieldTypeTitle[value],
}));

export const typeOptionsWithoutFieldArray = typeOptions.filter(
  (item) => item.value !== FormFieldType.FIELD_ARRAY
);

export const typeOptionsWithoutFieldArrayAndOutcomes =
  typeOptionsWithoutFieldArray.filter(
    (item) =>
      item.value !== FormFieldType.OUTCOMES &&
      item.value !== FormFieldType.OUTCOME_FIELDS
  );

export const typeOptionsWithoutOutcomes = typeOptions.filter(
  (item) =>
    item.value !== FormFieldType.OUTCOMES &&
    item.value !== FormFieldType.OUTCOME_FIELDS
);

// HEADING and TEXT are allowed in both nested contexts below as static
// content without collecting data or changing pagination.
export const fieldArrayDisallowedNestedFieldTypes = new Set<FormFieldType>([
  FormFieldType.FIELD_ARRAY,
  FormFieldType.OUTCOMES,
  FormFieldType.OUTCOME_FIELDS,
]);

export const outcomeFieldsDisallowedNestedFieldTypes = new Set<FormFieldType>([
  FormFieldType.FIELD_ARRAY,
  FormFieldType.OUTCOMES,
  FormFieldType.OUTCOME_FIELDS,
  FormFieldType.PAGE_BREAK,
]);

export const booleanOptions = [
  { label: 'Yes', value: 'true' },
  { label: 'No', value: 'false' },
];

export const fieldTypeTitle = (type: FormFieldType) =>
  FormFieldTypeTitle[type] ??
  type
    .split('_')
    .map((segment) => segment.charAt(0).toUpperCase() + segment.slice(1))
    .join(' ');
